import { useMemo } from "react";
import { useVersion } from "@ihs-widget/utils/hooks/useVersion";
import { useMatchTimeDisplay } from "@ihs-widget/utils/hooks/useMatchTimeDisplay";

interface MatchTimeData {
    minutes_played: number;
    seconds_played: number;
    is_running: boolean;
    home_score?: number;
    away_score?: number;
    isEnded?: boolean;
    isFullTime?: boolean;
    isHalfTime?: boolean;
    isExtraTimeFirstHalf?: boolean;
    isExtraTimeSecondHalf?: boolean;
    isHalfTimeOfExtraTime?: boolean;
    isPenalties?: boolean;
    isPreMatch?: boolean;
    isEndOfSecondHalf?: boolean;
    isEndOfExtraTime?: boolean;
    minute?: number;
    time_start?: string;
    currentPeriod?: number;
}

interface IOverviewScoreboardScoreProps {
    home_score: number | undefined;
    away_score: number | undefined;
    isEnded: boolean | undefined;
    isHalfTimeFirstEvent: boolean;
    isFullTimeFirstEvent: boolean;
    isEventAfterFullTime: boolean;
    isExtraTimeFirstEvent: boolean;
    isPenaltiesFirstEvent: boolean;
    isEventAfterPenalties: boolean;
    penaltyResult: string | null;
    penaltiesAreStarted: boolean;
    shouldShowPenaltyAfterMatchEnd?: boolean;
    matchClockData?: MatchTimeData | null;
}

export const OverviewScoreboardScore = ({ 
    home_score, 
    away_score, 
    isEnded, 
    isHalfTimeFirstEvent, 
    isFullTimeFirstEvent, 
    isExtraTimeFirstEvent,
    isEventAfterFullTime,
    isPenaltiesFirstEvent,
    isEventAfterPenalties,
    penaltyResult,
    penaltiesAreStarted,
    shouldShowPenaltyAfterMatchEnd,
    matchClockData
}: IOverviewScoreboardScoreProps) => {
    const { isV2 } = useVersion();

    const { isRunning, displayText } = useMatchTimeDisplay({
        matchClockData: matchClockData ? matchClockData : null,
        isEnabled: true,
        isEnded,
        isHalfTimeFirstEvent,
        isFullTimeFirstEvent,
        isEventAfterFullTime,
        isExtraTimeFirstEvent,
        isPenaltiesFirstEvent,
        isEventAfterPenalties,
        penaltyResult,
        penaltiesAreStarted,
        shouldShowPenaltyAfterMatchEnd,
        isTimerOn: false
    });
    
    const formattedResult = useMemo(() => {
        return home_score !== undefined && away_score !== undefined ? `${home_score} - ${away_score}` : '0 - 0';
    }, [home_score, away_score, isEnded]);

    const getTimeColor = (isHalfTimeFirstEvent: boolean, 
        isEnded: boolean | undefined,  
        isPenaltiesFirstEvent: boolean, 
        isEventAfterPenalties: boolean,
        matchClockData: MatchTimeData | null
    ) => {
        if ((isPenaltiesFirstEvent || isEventAfterPenalties) || isEnded || 
            matchClockData?.isPenalties || matchClockData?.isPreMatch ||
            matchClockData?.isFullTime || matchClockData?.isEnded ||
            matchClockData?.isEndOfSecondHalf || matchClockData?.isEndOfExtraTime || 
            matchClockData?.isHalfTimeOfExtraTime) {
            return 'text-error110 dark:text-error090';
        } else if (isHalfTimeFirstEvent && !isRunning || matchClockData?.isHalfTime) {
            return 'text-monoY';
        }
        return isV2 ? 'text-success120' : 'text-success110 dark:text-success120';
      };


    return (
        <div className={` h-[44px] mt-[12px] flex flex-col justify-center items-center 
        ${isV2 ? 'text-monoY' : 'mx-[16px] text-success110 dark:text-success120'}`}
        >
            <div 
                data-testid="overview-scoreboard-score"
                className='text-[28px] leading-[28px] font-medium font-unibet w-[68px] max-w-[68px] h-[28px] max-h-[28px] flex justify-center' 
                style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
                {formattedResult}
            </div>
            <div 
                data-testid="overview-scoreboard-elapsed-time"
                className={`text-[12px] leading-[16px] font-normal 
                    ${getTimeColor(
                        isHalfTimeFirstEvent, 
                        isEnded, 
                        isPenaltiesFirstEvent, 
                        isEventAfterPenalties,
                        matchClockData ?? null)}`}>
                {displayText}
            </div>
        </div>
    );
}
